import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { UploadCloud } from 'lucide-react';
import { SITE } from '../data/site';

const SERVICES = ['Roofing', 'Chimney', 'Siding', 'Gutters', 'Other'];
const MAX_UPLOAD_FILES = 2;
const EMPTY_FORM = {
  first: '', last: '', email: '', phone: '', services: [], city: '', zip: '', project: '',
};

// Returns an error message when the form is incomplete, otherwise null.
const validateEstimateForm = (form) => {
  const requiredText = [form.first, form.email, form.phone, form.city, form.zip, form.project];
  const hasEmptyField = requiredText.some((value) => !value.trim());
  if (hasEmptyField || form.services.length === 0) {
    return 'Please complete all required fields.';
  }
  return null;
};

// Persists the request locally (frontend prototype — no backend submission).
const saveEstimateRequest = (form, files) => {
  const all = JSON.parse(localStorage.getItem('estimateRequests') || '[]');
  const entry = { ...form, files: files.map((f) => f.name), at: new Date().toISOString() };
  localStorage.setItem('estimateRequests', JSON.stringify([...all, entry]));
};

const TextField = ({ label, type = 'text', value, onChange, testId }) => (
  <div>
    <Label className="font-heading font-semibold">{label}</Label>
    <Input type={type} className="mt-2" value={value} onChange={onChange} data-testid={testId} />
  </div>
);

const NameFields = ({ form, setForm }) => (
  <div>
    <Label className="font-heading font-semibold">Name *</Label>
    <div className="grid grid-cols-2 gap-4 mt-2">
      <div>
        <Input value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} data-testid="input-first-name" />
        <p className="text-xs text-muted-foreground mt-1">First</p>
      </div>
      <div>
        <Input value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} data-testid="input-last-name" />
        <p className="text-xs text-muted-foreground mt-1">Last</p>
      </div>
    </div>
  </div>
);

const ServiceCheckboxes = ({ selected, onToggle }) => (
  <div>
    <Label className="font-heading font-semibold">What service do you need? *</Label>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
      {SERVICES.map((s) => (
        <label key={s} className="flex items-center gap-2.5 cursor-pointer font-heading">
          <Checkbox checked={selected.includes(s)} onCheckedChange={() => onToggle(s)} data-testid={`service-checkbox-${s.toLowerCase()}`} />
          {s}
        </label>
      ))}
    </div>
  </div>
);

const UploadArea = ({ files, onFiles }) => (
  <div>
    <Label className="font-heading font-semibold">Upload Drawings / Pictures / Videos</Label>
    <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-input py-9 cursor-pointer hover:border-primary transition-colors" data-testid="upload-area">
      <UploadCloud className="w-8 h-8 text-muted-foreground" />
      <span className="mt-2 text-sm text-muted-foreground text-center px-4">
        Click or drag files to this area to upload. You can upload up to {MAX_UPLOAD_FILES} files.
      </span>
      <input
        type="file"
        multiple
        className="hidden"
        onChange={(e) => onFiles(Array.from(e.target.files || []).slice(0, MAX_UPLOAD_FILES))}
      />
      {files.length > 0 && (
        <span className="mt-2 text-sm text-foreground font-heading">{files.map((f) => f.name).join(', ')}</span>
      )}
    </label>
  </div>
);

const HelpSupport = () => (
  <div className="text-center mt-10">
    <h2 className="font-heading font-semibold text-xl">Help & Support</h2>
    <p className="mt-3 text-foreground/85">
      If you have any questions, please call us at <a href={SITE.phoneHref} className="font-bold text-primary">{SITE.phone}</a> or email us at{' '}
      <a href={`mailto:${SITE.email}`} className="font-bold text-primary">{SITE.email}</a>
    </p>
  </div>
);

export default function FreeEstimate() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    document.title = 'Free Roofing Estimate - Phoenix Roofing & Chimney';
  }, []);

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    const error = validateEstimateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    saveEstimateRequest(form, files);
    navigate('/thank-you');
  };

  return (
    <div className="bg-secondary py-16 min-h-[70vh]" data-testid="free-estimate-page">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center">
          <h1 className="section-title text-3xl md:text-4xl" data-testid="estimate-title">Request a Free in-House Appointment</h1>
          <p className="mt-4 text-lg text-foreground/85">Get a Free Estimate With Honest Pricing</p>
        </div>

        <form onSubmit={submit} className="bg-background shadow-card p-7 md:p-10 mt-10 space-y-6" data-testid="estimate-form">
          <NameFields form={form} setForm={setForm} />
          <TextField label="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="input-email" />
          <TextField label="Phone *" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} testId="input-phone" />
          <ServiceCheckboxes selected={form.services} onToggle={toggleService} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Town/City? *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} testId="input-city" />
            <TextField label="Zipcode *" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} testId="input-zip" />
          </div>
          <div>
            <Label className="font-heading font-semibold">Describe your project *</Label>
            <Textarea className="mt-2 min-h-[120px]" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="input-project" />
          </div>
          <UploadArea files={files} onFiles={setFiles} />
          <button type="submit" className="btn-cta w-full !py-4 text-base font-semibold" data-testid="estimate-submit">
            GET A FREE ESTIMATE!
          </button>
        </form>

        <HelpSupport />
      </div>
    </div>
  );
}
